import { useNavigate } from 'react-router-dom';
import { Button, H2, Paragraph, Spinner, Text, XStack, YStack } from 'tamagui';
import { AlertTriangle, Clock, ShieldAlert, Snowflake, Tag } from '@tamagui/lucide-icons';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from '../lib/notifications';
import type { AppNotificationDto, AppNotificationType } from '../lib/api';

export function Notifications() {
  const navigate = useNavigate();
  const { data, isLoading } = useNotifications(false);
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const rows = data ?? [];
  const hasUnread = rows.some((r) => !r.read);

  async function open(n: AppNotificationDto) {
    if (!n.read) {
      try {
        await markRead.mutateAsync(n.id);
      } catch {
        // Ignore — navigation should still happen.
      }
    }
    if (n.vehicleId) {
      navigate(`/vehicles/${n.vehicleId}`);
    }
  }

  if (isLoading) {
    return (
      <YStack alignItems="center" padding="$6">
        <Spinner />
      </YStack>
    );
  }

  return (
    <YStack gap="$4" maxWidth={800} width="100%">
      <XStack justifyContent="space-between" alignItems="center">
        <H2>Notifications</H2>
        {hasUnread ? (
          <Button size="$2" onPress={() => markAllRead.mutate()}>
            Mark all read
          </Button>
        ) : null}
      </XStack>

      {rows.length === 0 ? (
        <YStack padding="$6" alignItems="center">
          <Paragraph color="$color10">You're all caught up.</Paragraph>
        </YStack>
      ) : (
        <YStack gap="$2">
          {rows.map((n) => (
            <NotificationRow key={n.id} notification={n} onOpen={() => void open(n)} />
          ))}
        </YStack>
      )}
    </YStack>
  );
}

// Leading-icon size (px) for a notification row.
const ICON_SIZE = 20;
// Timestamp / type-label micro-copy size (px), per DESIGN.md's 12px label step.
const META_FONT_SIZE = 12;
// Read rows dim slightly so unread rows stay the more prominent surface once the
// type tint (not a background step) owns the row fill.
const READ_OPACITY = 0.65;

// Two visual tones. Attention (Overdue, Recall) reads as "needs attention" via a
// danger-tinted fill + red icon/label; calm (Due soon, Deal, Seasonal) reads
// informational via a neutral raised tint + muted icon/label. No tone uses Signal
// Blue, and the only hue is the danger tint DESIGN.md sanctions for attention, so
// the Status-Ramp stays reserved for service urgency.
type NotifTone = 'attention' | 'calm';

interface ToneStyle {
  tint: string;
  iconColor: string;
  labelColor: string;
}

const TONE_STYLES: Record<NotifTone, ToneStyle> = {
  attention: { tint: '$red3', iconColor: '$red10', labelColor: '$red11' },
  calm: { tint: '$color3', iconColor: '$color10', labelColor: '$color10' },
};

type NotifIcon = typeof AlertTriangle;

interface NotifTypeMeta {
  label: string;
  Icon: NotifIcon;
  tone: NotifTone;
}

// The five notification types → their leading icon, exact text type label, and
// tone. The label strings are this feature's contract (see the spec's
// audit_targets); do not localize or abbreviate them here.
const NOTIFICATION_META: Record<AppNotificationType, NotifTypeMeta> = {
  service_overdue: { label: 'Overdue', Icon: AlertTriangle, tone: 'attention' },
  recall: { label: 'Recall', Icon: ShieldAlert, tone: 'attention' },
  service_due_soon: { label: 'Due soon', Icon: Clock, tone: 'calm' },
  deal: { label: 'Deal', Icon: Tag, tone: 'calm' },
  seasonal: { label: 'Seasonal', Icon: Snowflake, tone: 'calm' },
};

function NotificationRow({
  notification,
  onOpen,
}: {
  notification: AppNotificationDto;
  onOpen: () => void;
}) {
  const meta = NOTIFICATION_META[notification.type];
  const tone = TONE_STYLES[meta.tone];
  const Icon = meta.Icon;
  return (
    <XStack
      onPress={onOpen}
      cursor="pointer"
      padding="$3"
      borderRadius="$4"
      gap="$3"
      alignItems="center"
      backgroundColor={tone.tint}
      opacity={notification.read ? READ_OPACITY : 1}
      hoverStyle={{ backgroundColor: '$backgroundPress' }}
    >
      <Icon size={ICON_SIZE} color={tone.iconColor} />
      <YStack flex={1} gap="$1">
        <XStack justifyContent="space-between" alignItems="center" gap="$2">
          <Text fontWeight={notification.read ? '400' : '700'} flexShrink={1}>
            {notification.title}
          </Text>
          <Text color="$color10" fontSize={META_FONT_SIZE}>
            {formatRelative(notification.sentAt)}
          </Text>
        </XStack>
        <Text color={tone.labelColor} fontWeight="600" fontSize={META_FONT_SIZE}>
          {meta.label}
        </Text>
        <Paragraph color="$color11" size="$2">
          {notification.body}
        </Paragraph>
      </YStack>
    </XStack>
  );
}

function formatRelative(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}
